import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { IconButton } from './IconButton'
import styles from './Toast.module.css'

export type ToastTone = 'success' | 'danger' | 'info'

interface Toast {
  readonly id: number
  readonly tone: ToastTone
  readonly message: string
}

interface ToastApi {
  readonly notify: (tone: ToastTone, message: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}

const DISMISS_AFTER_MS = 6000

export interface ToastProviderProps {
  readonly dismissLabel: string
  readonly children: ReactNode
}

/**
 * A single live region owns every toast. Rendering one live region per toast
 * is the usual mistake: a region added to the DOM at the same moment as its
 * content is frequently not announced at all, because the assistive tech
 * never observed it change.
 *
 * `role="status"` (polite) rather than `alert` (assertive): these confirm
 * completed actions and must not interrupt what the user is reading. A
 * genuinely urgent message belongs in the field's own `role="alert"`.
 */
export function ToastProvider({ dismissLabel, children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<readonly Toast[]>([])
  const nextId = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const notify = useCallback(
    (tone: ToastTone, message: string) => {
      const id = nextId.current++
      setToasts((current) => [...current, { id, tone, message }])
      setTimeout(() => dismiss(id), DISMISS_AFTER_MS)
    },
    [dismiss],
  )

  const api = useMemo(() => ({ notify }), [notify])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.region} role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={[styles.toast, styles[t.tone]].filter(Boolean).join(' ')}>
            <span className={styles.message}>{t.message}</span>
            <IconButton label={dismissLabel} size="sm" onClick={() => dismiss(t.id)}>
              ✕
            </IconButton>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
